import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MilaMultiSelectHierarchyFetcher, MilaMultiSelectOption } from '../../mila-multi-select/mila-multi-select-option.model';

/**
 * One row inside MilaMultiSelectHierarchy's dropdown, and recursively, one
 * row inside its own expanded children same component nested into itself.
 * Children are lazy-loaded on first expand and cached afterward (collapsing
 * just hides them, it doesn't forget them), one flat page per node a
 * node's own children are a bounded branch, not something that needs
 * infinite scroll the way the root search list does.
 */
@Component({
  selector: 'mila-multi-select-tree-item',
  standalone: false,
  templateUrl: './mila-multi-select-tree-item.html',
  styleUrl: './mila-multi-select-tree-item.scss',
})
export class MilaMultiSelectTreeItem {
  @Input({ required: true }) item!: MilaMultiSelectOption;
  @Input({ required: true }) fetchPage!: MilaMultiSelectHierarchyFetcher;
  @Input() selectedIds: ReadonlySet<MilaMultiSelectOption['id']> = new Set();
  /** Indentation level 0 at the root, +1 per nested level. */
  @Input() depth = 0;

  @Output() select = new EventEmitter<MilaMultiSelectOption>();

  private readonly _destroy$ = new Subject<void>();

  expanded = false;
  isLoading = false;
  children: MilaMultiSelectOption[] | null = null;

  get isSelected(): boolean {
    return this.selectedIds.has(this.item.id);
  }

  toggleExpand(event: Event): void {
    event.stopPropagation();

    if (this.expanded) {
      this.expanded = false;
      return;
    }

    if (this.children) {
      this.expanded = true;
      return;
    }

    this.isLoading = true;
    this.fetchPage('', 0, this.item.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: res => {
          this.children = res.items;
          this.expanded = this.children.length > 0;
          this.isLoading = false;
        },
        error: () => {
          this.children = [];
          this.isLoading = false;
        },
      });
  }

  onSelect(event: Event): void {
    event.stopPropagation();
    this.select.emit(this.item);
  }

  onChildSelect(option: MilaMultiSelectOption): void {
    this.select.emit(option);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
